import { useEffect, useState, useRef } from "react";
import {
    createCartMutation,
    patchGroupCart,
    useGroupCart,
    commitGroupParticipant,
    joinGroupAsParticipant,
    leaveGroupAsParticipant,
} from "../Api/cartApi";
import { generateNewCartData } from "../Helpers/cookie-utils";
import CartSession from "./cartSession";
import { generateUuid, encodeString } from "../Helpers/util";
import { useCartContext } from "./cartContext";
import useFeatureFlags from "../Helpers/hooks/useFeatureFlags";
import moment from "moment";
import { useDeliveryTimes } from "~/assets/js/Api/deliveryTimesApi";
import { useFormik } from "formik";
import { subsFromDate } from "../Helpers/date-utils";

const transferItemsFromPreviousCart = (cart) => {
    if (CartSession.isSet()) {
        const previousCart = CartSession.get();
        return {
            ...cart,
            items: previousCart.items,
        };
    }

    return cart;
};

const checkGroupCartUrl = () => {
    const currentUrl = window.location.href;

    if (!currentUrl.includes("/group-cart")) {
        return false;
    }

    const codeRegex = /group-cart\/([A-Za-z0-9]{8})\//;
    const match = currentUrl.match(codeRegex);

    return match !== null;
};

const getUniqueGroupIdFromUrl = () => {
    const url = window.location.href;

    const codeRegex = /group-cart\/([A-Za-z0-9]{8})\//;
    const match = url.match(codeRegex);

    if (match && match[1]) {
        return match[1];
    }

    return undefined;
};

const GROUP_ORDER_STATES = {
    OPEN: "OPEN",
    CLOSED: "CLOSED",
    CANCELLED: "CANCELLED",
};

export const GROUP_ORDER_BUFFER = { value: 20, unit: "m" };
export const MEMBER_BUFFER = { value: 10, unit: "m" };

const HALF_MINUTE = 1 * 30 * 1000;

// Wrapper around Cart Context that handles the GroupOrder feature
const useGroupOrder = () => {
    const [submitting, setSubmitting] = useState(false);
    const { actions, cart } = useCartContext();
    const { FEATURE_GROUP_ORDER } = useFeatureFlags();
    const [isGroupOrderURL, setIsGroupOrderURL] = useState(false);
    const isGroupOrder = cart?.group_cart;
    const [memberGroupOrderTimeOut, setMemberGroupOrderTimeOut] =
        useState(false);
    const memberTimeoutFlag = useRef(false);
    const ownerTimeoutFlag = useRef(false);
    const [ownerGroupOrderTimeOut, setOwnerGroupOrderTimeOut] = useState(false);

    const [timeslotPopupOpen, setOpenTimeslotPopup] = useState(false);
    const { earliestTimeslot, refetchTimeslots } = useDeliveryTimes({
        buffer: GROUP_ORDER_BUFFER,
    });

    const isOwner = (() => {
        const cookie = window.cookie.get();
        const cart = CartSession.get();

        if (!cart?.group_cart || !cookie.participant) {
            return false;
        }

        return (
            encodeString(`owner:${cookie.participant.uid}`) ===
            cookie.participant?.hash
        );
    })();

    const formik = useFormik({
        initialValues: {
            timeslot: null,
            earlyOnLocation: false,
        },
        onSubmit: async ({ timeslot }) => {
            const selectedSlot = timeslot || earliestTimeslot;
            const cookie = window.cookie.get();
            formik.setFieldValue("timeslot", selectedSlot);

            const response = await patchGroupCart({
                uid: groupCartData.group_uid,
                status: GROUP_ORDER_STATES.OPEN,
                owner_uid: cookie.participant.uid,
                timeslot: {
                    slot_id: timeslot.id,
                    slot_begin: timeslot.begin,
                    slot_end: timeslot.end,
                },
            });

            response.ok && actions.updateTimeSlot(timeslot);
        },
    });

    useEffect(() => {
        const isJoinGroupOrderUrl = checkGroupCartUrl();
        setIsGroupOrderURL(isJoinGroupOrderUrl);
    }, []);

    const updateCartBasedOnGroupCart = (groupOrderData) => {
        const isGroupOpen = groupOrderData.status === GROUP_ORDER_STATES.OPEN;
        const isParticipant = !isOwner;

        if (isGroupOpen && isOwner) {
            updateCartItemsWithParticipants(groupOrderData);
        }

        if (!isGroupOpen && isParticipant) {
            leaveParticipant();
        }
    };

    const updateCartItemsWithParticipants = (groupOrderData) => {
        const cart = CartSession.get();
        const cartOwnerUid = cart.owner_uid;

        const commitedParticipants =
            groupOrderData?.participants?.filter(
                (participant) => participant.status === "COMMITTED"
            ) || [];

        let items = cart?.items || [];

        // syncs participant items with cartApi
        commitedParticipants.forEach((participant) => {
            // purge old items
            const itemsWithoutParticipant = items.filter(
                (item) => item.owner_uid !== participant.uid
            );

            items = itemsWithoutParticipant;

            // add current commited items
            participant.cart_items.forEach((item) => {
                items.push(item);
            });
        });

        // Remove participants that left the group
        const participantsUidsInGroup = [
            ...groupOrderData?.participants.map(
                (participant) => participant.uid
            ),
            cartOwnerUid,
        ];

        const participantsUidsInCart = items?.map((item) => item.owner_uid);

        participantsUidsInCart.forEach((owner_uid) => {
            // If owner item is in cart but no longer participant of group order
            const isOrphanItem = !participantsUidsInGroup.includes(owner_uid);
            if (isOrphanItem) {
                items = items.filter((item) => item.owner_uid !== owner_uid);
            }
        });

        actions.addMultipleItemsToCart(items);
    };

    const { data: groupCartData, loading: groupCartLoading } = useGroupCart({
        uid: getUniqueGroupIdFromUrl() || CartSession.get()?.group_uid,
        enabled:
            FEATURE_GROUP_ORDER &&
            (isGroupOrderURL || CartSession.get()?.group_cart),
        owner_uid: isOwner && window.cookie.get().participant.uid,
        updateCartCallback: updateCartBasedOnGroupCart,
    });

    const checkFeatureFlag = () => {
        if (!FEATURE_GROUP_ORDER) {
            throw new Error("FEATURE_GROUP_ORDER disabled");
        }
    };

    const createGroupOrder = async ({ name, timeslot, earlyOnLocation }) => {
        checkFeatureFlag();

        const ownerInfo = {
            owner_name: name,
            owner_uid: generateUuid(),
        };

        let groupOrderCart = generateNewCartData();
        groupOrderCart = transferItemsFromPreviousCart(groupOrderCart);
        groupOrderCart = {
            group_cart: true, // <- inits the group order
            ...groupOrderCart,
            ...ownerInfo,
            slot_id: timeslot.id,
            slot_begin: timeslot.begin,
            slot_end: timeslot.end,
            items:
                groupOrderCart?.items?.map((item) => ({
                    ...item,
                    ...ownerInfo,
                })) || [],
        };

        if (groupOrderCart.dispatch_type === "delivery") {
            groupOrderCart = {
                ...groupOrderCart,
                shipping: {
                    ...groupOrderCart.shipping,
                    earlier_allowed: earlyOnLocation,
                },
            };
        }

        try {
            setSubmitting(true);

            updateParticipantInfo({
                participantUid: ownerInfo.owner_uid,
                participantName: ownerInfo.owner_name,
                groupOrderCart,
                isOwner: true,
            });

            const response = await createCartMutation(groupOrderCart);
            if (!response.ok) {
                throw new Error(
                    `Error creating new cart | details: ${response?.msg || ""}`
                );
            }

            if (actions) {
                actions.updateCartState(response.cart);
            } else {
                CartSession.update(response.cart);
            }

            return response;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error.message);
            return { ok: false, data: error.message };
        } finally {
            setSubmitting(false);
        }
    };

    const generateGroupOrderInviteUrl = () => {
        if (!groupCartData?.group_uid) {
            return "";
        }

        return `${window.location.origin}/group-cart/${groupCartData?.group_uid}/`;
    };

    const abortGroupOrder = async () => {
        try {
            if (!isOwner) {
                throw new Error("Not owner");
            }

            const cookie = window.cookie.get();

            setSubmitting(true);

            await patchGroupCart({
                uid: groupCartData?.group_uid,
                status: GROUP_ORDER_STATES.CANCELLED,
                owner_uid: cookie.participant.uid,
            });

            const { cart } = await createCartMutation(generateNewCartData());
            actions.updateCartState(cart);

            return { ok: true, data: cart };
        } catch (error) {
            return { ok: false, data: error.message };
        } finally {
            setSubmitting(false);
        }
    };

    const closeGroupOrder = async () => {
        try {
            if (!isOwner) {
                throw new Error("Not owner");
            }

            const cookie = window.cookie.get();

            setSubmitting(true);
            await patchGroupCart({
                uid: groupCartData?.group_uid,
                status: GROUP_ORDER_STATES.CLOSED,
                owner_uid: cookie.participant.uid,
            });

            const { cart } = await createCartMutation(generateNewCartData());
            actions.updateCartState(cart);
            return { ok: true, data: cart };
        } catch (error) {
            return { ok: false, data: error.message };
        } finally {
            setSubmitting(false);
        }
    };

    const joinGroupOrder = async ({ participantName }) => {
        checkFeatureFlag();

        try {
            setSubmitting(true);

            const participantUid = generateUuid();

            updateParticipantInfo({
                participantUid,
                participantName,
                groupOrderCart: groupCartData,
            });

            const response = await joinGroupAsParticipant({
                groupUid: groupCartData?.group_uid,
                participantName,
                participantUid,
            });

            if (!response.ok) {
                return { ok: false };
            }

            const previousCart = await actions.getCart();

            const {
                shipping_address: address,
                shipping_city: city,
                shipping_postcode: postcode,
                shipping_country: country,
                shipping_lat: lat,
                shipping_lon: lon,
                dispatch_type,
                group_uid,
                slot_begin,
                slot_end,
                slot_id,
                dispatch_target_id,
                store_id,
            } = groupCartData;

            const participantCart = {
                ...previousCart,
                dispatch_type,
                group_uid,
                slot_id,
                slot_begin,
                slot_end,
                group_cart: true,
                area_id: dispatch_target_id,
                store_id,
                items: [],
            };

            if (dispatch_type === "delivery") {
                participantCart.shipping = {
                    address,
                    city,
                    postcode,
                    country,
                    lat,
                    lon,
                    earlier_allowed: true, // irrelevant, but required
                };
            }

            actions.updateCartState(participantCart);

            return { ok: true };
        } catch (error) {
            return { ok: false };
        } finally {
            setSubmitting(false);
        }
    };

    const updateParticipantInfo = ({
        participantName,
        participantUid,
        groupOrderCart,
        isOwner = false,
    }) => {
        const participantType = isOwner ? "owner" : "member";

        const participantInfo = {
            name: participantName,
            uid: participantUid,
            hash: encodeString(`${participantType}:${participantUid}`),
        };

        const cookie = window.cookie.get();

        // Default cookie area info to Essen pickup so the app works
        if (!cookie.area && !isOwner) {
            cookie.area = {
                id: 1,
                name: "Abholung Essen",
                store_id: 1,
                type: "pickup",
            };
        }

        window.cookie.update({
            ...cookie,
            participant: participantInfo,
            groupStore: groupOrderCart?.store_id || cookie.area.store_id,
        });
    };

    const commitParticipant = async () => {
        checkFeatureFlag();

        try {
            setSubmitting(true);
            if (isOwner) {
                throw new Error("Not Participant!");
            }

            const cookie = window.cookie.get();

            if (!cookie.participant) {
                throw new Error("No ParticipantData!");
            }

            const participantData = cookie.participant;

            setSubmitting(true);

            const commitData = {
                groupUid: groupCartData?.group_uid,
                participantUid: participantData.uid,
                participantName: participantData.name,
                items: cart?.items,
                customer_id: cart?.customer_id,
            };

            await commitGroupParticipant(commitData);

            return { ok: true };
        } catch (error) {
            return { ok: false };
        } finally {
            setSubmitting(false);
        }
    };

    const leaveParticipant = async () => {
        try {
            if (isOwner) {
                throw new Error("Not Participant!");
            }

            const cookie = window.cookie.get();

            if (!cookie.participant) {
                throw new Error("No ParticipantData!");
            }

            const participantData = cookie.participant;
            setSubmitting(true);
            const response = await leaveGroupAsParticipant({
                groupUid: groupCartData?.group_uid,
                participantUid: participantData.uid,
            });

            if (!response.ok) {
                throw new Error("Api response error");
            }

            CartSession.clear();
            window.location.href = "/warenkorb/";
            return { ok: true };
        } catch (error) {
            setSubmitting(false);
            return { ok: false };
        }
    };

    const memberOrderTimeOut = () => {
        const isParticipant = !isOwner;
        if (!isParticipant) return;

        const hasValidUntill = groupCartData?.slot_valid_until;

        const notificationWasNotDisplayed = !memberTimeoutFlag.current;

        if (hasValidUntill && notificationWasNotDisplayed) {
            const slotTime = subsFromDate({
                date: groupCartData.slot_valid_until,
                unit: "m",
                value: 15,
            });

            const currentTime = moment();

            if (currentTime.isAfter(slotTime)) {
                setMemberGroupOrderTimeOut(true);
                memberTimeoutFlag.current = true;
            }
        }
    };

    const ownerOrderTimeOut = () => {
        if (!isOwner) return;

        const validTimeSlot = moment(groupCartData?.slot_valid_until);
        const currentTime = moment();
        const isSlotExpired = currentTime.isAfter(validTimeSlot);
        const notificationWasNotDisplayed = !ownerTimeoutFlag.current;

        if (isSlotExpired && notificationWasNotDisplayed) {
            setOwnerGroupOrderTimeOut(true);
            ownerTimeoutFlag.current = true;
        }
    };

    const ownerChangeTimeSlot = () => {
        setOwnerGroupOrderTimeOut(false);
        refetchTimeslots();
        setOpenTimeslotPopup(true);
    };

    const onTimeslotUpdate = ({ slot, earlyOnLocation }) => {
        setOpenTimeslotPopup(false);
        ownerTimeoutFlag.current = false;
        formik.setValues({
            earlyOnLocation,
            timeslot: slot,
        });
        formik.submitForm();
    };

    useEffect(() => {
        const memberTimeoutReminder = setInterval(() => {
            memberOrderTimeOut();
            ownerOrderTimeOut();
        }, HALF_MINUTE);
        return () => clearInterval(memberTimeoutReminder);
    }, [groupCartData]);

    return {
        groupOrderData: cart,
        groupCartData,
        memberGroupOrderTimeOut,
        ownerGroupOrderTimeOut,
        setOwnerGroupOrderTimeOut,
        setMemberGroupOrderTimeOut,
        createGroupOrder,
        abortGroupOrder,
        loading: submitting || groupCartLoading,
        inviteUrl: generateGroupOrderInviteUrl(),
        isOwner: isGroupOrder && isOwner,
        isParticipant: isGroupOrder && !isOwner,
        closeGroupOrder,
        commitParticipant,
        isGroupOrderURL,
        setIsGroupOrderURL,
        joinGroupOrder,
        leaveParticipant,
        isGroupOrder,
        ownerChangeTimeSlot,
        timeslotPopupOpen,
        setOpenTimeslotPopup,
        earliestTimeslot,
        onTimeslotUpdate,
    };
};

export default useGroupOrder;
